import {
  PapiDeviation,
  PapiSellerDiscount,
  PapiExtendedDeviation,
  PapiRequestDaSharedDeviationFetch,
} from '@wix/da-papi-types';

import { getSource, isGifDeviation, isVideoDeviation } from '../DeviationViews/_helpers';
import DeviationMediaService from './DeviationMediaService';

import { dollarsToPoints } from './points';
import { formatDollars } from './formatting';

export type AllowedDeviationFetchTypes = PapiRequestDaSharedDeviationFetch['type'];

export function elmoSaysThisMightBeADeviationUrl(
  url: string
): PapiRequestDaSharedDeviationFetch | undefined {
  if (!url) {
    return;
  }
  /*
    1. :thumbcode: - the deviation id may come from a thumb code
    :thumb123456:
  */
  let matches = url.match(/^:thumb(\d+):$/);
  if (matches) {
    return { deviationid: parseInt(matches[1]), type: 'art' };
  }

  /*
    2. supebrowse link -- HAS TO GO FIRST because SB links can be appended to OTHER valid links
    URL scheme:   https://*.deviantart.com/*#/d*
    https://randomduck.deviantart.com/gallery/#/d2k5cau
  */
  matches = url.match(/deviantart\.(com|lan).*#\/d([0-9a-z]+)/);
  if (matches) {
    return {
      deviationid: parseInt(parseInt(matches[2], 36).toString(10)),
      type: 'art',
    };
  }

  /*
    3a. subdomainfree canonical link
    URL scheme:   https://www.deviantart.com/user/art/*(*)
    https://www.deviantart.com/randomduck/art/test-ignore-589-154773894?q=portal%3A%2F%2Fdeviations%2F%3Fcategory.order%3Dfresh%26gs.mode%3Dthumbs&qo=2
  */
  matches = url.match(
    /deviantart\.(com|lan)\/((?:[0-9A-Za-z-_]+))\/((?:art|journal|status-update|commission|poll|tier))\/.*-(\d+)/
  );
  if (matches) {
    return {
      deviationid: parseInt(matches[4]),
      type: (matches[3] || 'art') as AllowedDeviationFetchTypes,
      username: matches[2],
    };
  }

  /*
    3b. canonical link
    URL scheme:   https://*.deviantart.com/art/*(*)
    https://randomduck.deviantart.com/art/test-ignore-589-154773894?q=portal%3A%2F%2Fdeviations%2F%3Fcategory.order%3Dfresh%26gs.mode%3Dthumbs&qo=2
  */
  matches = url.match(/deviantart\.(com|lan)\/(?:art|journal)\/.*-(\d+)/);
  if (matches) {
    return {
      deviationid: parseInt(matches[2]),
      type: 'art',
    };
  }

  /*
    4. fav.me  link
    URL scheme:   https://fav.me/d[devid] where devid is in base 36
    https://fav.me/d2k5cau

    URL scheme:   https://fav.me/[devid] where devid is in base 10
    https://fav.me/750477558
  */
  matches = url.match(/fav\.me\/d([0-9a-z]+)/);
  if (matches) {
    return {
      deviationid: parseInt(parseInt(matches[1], 36).toString(10)),
      type: 'art',
    };
  }

  matches = url.match(/fav\.me\/([0-9]+)/);
  if (matches) {
    return {
      deviationid: parseInt(matches[1]),
      type: 'art',
    };
  }

  /*
    5. really old link
    URL scheme:   https://www.deviantart.com/deviation/*
    https://www.deviantart.com/deviation/154773894/
  */
  matches = url.match(/www\.deviantart\.(com|lan)\/deviation\/(\d+)/);
  if (matches) {
    return {
      deviationid: parseInt(matches[2]),
      type: 'art',
    };
  }

  /*
    6. really, really old link
    URL scheme:   https://www.deviantart.com/view/*
    https://www.deviantart.com/view/154773894/
  */
  matches = url.match(/www\.deviantart\.(com|lan)\/view\/(\d+)/);
  if (matches) {
    return {
      deviationid: parseInt(matches[2]),
      type: 'art',
    };
  }

  /*
    7. sta.sh short link
    URL scheme:   https://sta.sh/0*
    https://sta.sh/018bxsl3dm4o
  */
  matches = url.match(/sta\.(sh|nk)\/0([0-9a-z]+)/);
  if (matches) {
    return {
      deviationid: parseInt(parseInt(matches[2], 36).toString(10)),
      type: 'art',
    };
  }

  /*
    8. sta.sh long link
    URL scheme:   https://sta.sh/1*
    https://sta.sh/14502318545176648
  */
  matches = url.match(/sta\.(sh|nk)\/1([0-9a-z]+)/);
  if (matches) {
    return {
      deviationid: parseInt(matches[2]),
      type: 'art',
    };
  }

  /*
    9. sta.sh eclipsed link
    URL scheme:   https://www.deviantart.com/stash/0*
    https://deviantart.com/stash/018bxsl3dm4o
  */
  matches = url.match(/www\.deviantart\.(com|lan)\/stash\/0([0-9a-z]+)/);
  if (matches) {
    return {
      deviationid: parseInt(parseInt(matches[2], 36).toString(10)),
      type: 'art',
    };
  }
}

/** returns 'video' or 'gif' if the deviation is either, undefined if neither */
export function isVideoOrGif(deviation: PapiDeviation): 'video' | 'gif' | undefined {
  if (isVideoDeviation(deviation)) {
    return 'video';
  }
  if (isGifDeviation(deviation)) {
    return 'gif';
  }
}

/** returns the deviation's duration if it's a video, undefined if not */
export function getVideoDuration(deviation: PapiDeviation): number | undefined {
  const videoFile = getSource(DeviationMediaService.getMediaByType(deviation, 'video'));
  return videoFile?.duration;
}

export type DeviationOriginalFile = PapiExtendedDeviation['originalFile'];

/** returns the extended deviation's original file struct (if available) */
export function getDeviationOriginalFile(deviation): DeviationOriginalFile | undefined {
  return deviation?.extended?.originalFile;
}

export function getDeviationPrice(deviation: PapiDeviation) {
  let dollarsPrice;
  let pointsPrice;

  if (deviation.commission) {
    dollarsPrice = deviation.commission.dollarsCost;
    pointsPrice = deviation.commission.pointsCost;
  } else if (deviation.pcp) {
    dollarsPrice = deviation.pcp.downloadDollarPrice;
    pointsPrice = deviation.pcp.downloadPrice;
  } else if (deviation.isAdoptable) {
    dollarsPrice = deviation.adoptable?.fixedPrice?.fixedPrice || 0;
    pointsPrice = dollarsToPoints(dollarsPrice);
  } else if (deviation.tier) {
    dollarsPrice = deviation.tier.dollarPrice;
    pointsPrice = dollarsToPoints(dollarsPrice);
  } else if (deviation.premiumFolderData) {
    dollarsPrice = deviation.premiumFolderData.dollarPrice;
    pointsPrice = deviation.premiumFolderData.pointsPrice;
  }

  return { dollarsPrice, pointsPrice };
}

export function getDiscountedPrice(price?: number, discount?: PapiSellerDiscount) {
  let discountedPrice = price;
  if (price !== undefined && discount?.amount?.usd) {
    discountedPrice = price - discount?.amount?.usd;
  }

  return {
    price,
    discountedPrice,
  };
}

export function getDeviationDiscount(
  deviation: PapiDeviation,
  options?: { optionalDecimalDigits: boolean }
): {
  dollarAmount?: number;
  formattedDollarAmount?: string;
  dollarAmountAfterDiscount?: number;
  formattedDollarAmountAfterDiscount?: string;
  discountDollarAmount?: number;
  formattedDiscountDollarAmount?: string;
  discount?: PapiSellerDiscount;
} {
  let discount: PapiSellerDiscount | undefined;
  const { dollarsPrice: dollarAmount } = getDeviationPrice(deviation);
  const formatOptions = options?.optionalDecimalDigits
    ? {
        useGrouping: true,
      }
    : undefined;

  if (deviation?.isAdoptable) {
    discount = deviation?.adoptable?.fixedPrice?.discount;
  } else if (deviation?.pcp) {
    discount = deviation?.pcp?.discount;
  } else if (deviation?.tier) {
    discount = deviation?.tier?.discount;
  }

  const { price, discountedPrice } = getDiscountedPrice(dollarAmount, discount);
  const formattedDollarAmount = formatDollars(price, formatOptions);
  const formattedDollarAmountAfterDiscount = formatDollars(discountedPrice, formatOptions);
  const discountDollarAmount = discount?.amount?.usd;
  const formattedDiscountDollarAmount = formatDollars(discountDollarAmount, formatOptions);

  return {
    dollarAmount: price,
    formattedDollarAmount,
    dollarAmountAfterDiscount: discountedPrice,
    formattedDollarAmountAfterDiscount,
    discountDollarAmount,
    formattedDiscountDollarAmount,
    discount,
  };
}

export function getDeviationSaleDetails(
  deviation: PapiDeviation,
  options?: { optionalDecimalDigits: boolean }
): {
  dollarAmount?: number;
  formattedDollarAmount?: string;
  dollarAmountAfterDiscount?: number;
  formattedDollarAmountAfterDiscount?: string;
  eligibleDiscountDollarAmount?: number;
  formattedEligibleDiscountDollarAmount?: string;
  eligibleDiscount?: PapiSellerDiscount;
  notEligibleDiscounts?: PapiSellerDiscount[];
  isDiscountApplied: boolean;
  hasDiscounts: boolean;
  allDiscounts: PapiSellerDiscount[];
} {
  let eligibleDiscount: PapiSellerDiscount | undefined;
  let notEligibleDiscounts: PapiSellerDiscount[] | undefined;
  const formatOptions = options?.optionalDecimalDigits
    ? {
        useGrouping: true,
      }
    : undefined;

  if (deviation?.isAdoptable) {
    ({ eligibleDiscount, notEligibleDiscounts } =
      deviation?.extended?.saleDetails?.adoptableFixed || {});
  } else if (deviation?.pcp) {
    ({ eligibleDiscount, notEligibleDiscounts } = deviation?.extended?.saleDetails?.pcp || {});
  } else if (deviation?.tier || deviation?.primaryTier) {
    ({ eligibleDiscount, notEligibleDiscounts } =
      deviation?.extended?.saleDetails?.subscription || {});
  } else if (deviation?.premiumFolderData) {
    ({ eligibleDiscount, notEligibleDiscounts } =
      deviation?.extended?.saleDetails?.premiumFolder || {});
  }

  if (!eligibleDiscount) {
    if (deviation?.primaryTier && deviation?.extended?.saleDetails?.subscription) {
      ({ eligibleDiscount, notEligibleDiscounts } =
        deviation?.extended?.saleDetails?.subscription || {});
    } else if (deviation?.premiumFolderData && deviation?.extended?.saleDetails?.premiumFolder) {
      ({ eligibleDiscount, notEligibleDiscounts } =
        deviation?.extended?.saleDetails?.premiumFolder || {});
    }
  }

  const { dollarsPrice } = getDeviationPrice(deviation);

  const { price: dollarAmount, discountedPrice: dollarAmountAfterDiscount } = getDiscountedPrice(
    dollarsPrice,
    eligibleDiscount
  );
  const formattedDollarAmount = formatDollars(dollarAmount, formatOptions);
  const formattedDollarAmountAfterDiscount = formatDollars(
    dollarAmountAfterDiscount,
    formatOptions
  );
  const eligibleDiscountDollarAmount = eligibleDiscount?.amount?.usd;
  const formattedEligibleDiscountDollarAmount = formatDollars(
    eligibleDiscountDollarAmount,
    formatOptions
  );
  const allDiscounts = [...(notEligibleDiscounts || []), eligibleDiscount].filter(
    Boolean
  ) as PapiSellerDiscount[];

  return {
    dollarAmount,
    formattedDollarAmount,
    dollarAmountAfterDiscount,
    formattedDollarAmountAfterDiscount,
    eligibleDiscountDollarAmount,
    formattedEligibleDiscountDollarAmount,
    eligibleDiscount,
    notEligibleDiscounts,
    isDiscountApplied: !!eligibleDiscount,
    hasDiscounts: !!allDiscounts.length,
    allDiscounts,
  };
}

export function getDeviationIsWixelSubmission(deviation: PapiDeviation) {
  return deviation.extended?.submissionApps?.includes('wixel');
}

export function getDeviationIsDrawSubmission(deviation: PapiDeviation) {
  return deviation.extended?.submissionApps?.includes('murodraw');
}

export function getTierIsOnSale(tier) {
  return tier?.extended?.saleDetails?.subscription?.eligibleDiscount || tier?.tier?.discount;
}
